import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Card, CardContent, Typography, Box, Button, Chip, CircularProgress } from "@mui/material";
import EventAvailableIcon from "@mui/icons-material/EventAvailable";
import VideocamIcon from "@mui/icons-material/Videocam";
import CloseIcon from "@mui/icons-material/Close";
import { skipScheduledCall } from "../utils/firebase";

const PLATFORM_LABELS = {
  google_meet: "Google Meet",
  zoom: "Zoom",
  teams: "Microsoft Teams",
  webex: "Webex",
};

function formatWhen(timestamp, t) {
  if (!timestamp) return "—";
  const d = new Date(timestamp);
  const now = new Date();
  const diffMs = d.getTime() - now.getTime();
  const dateLabel = d.toLocaleDateString(undefined, {
    weekday: "short",
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
  if (diffMs <= 0) return dateLabel;
  const mins = Math.round(diffMs / 60000);
  let countdown;
  if (mins < 60) countdown = t("upcoming.inMinutes", { count: mins });
  else if (mins < 60 * 24) countdown = t("upcoming.inHours", { count: Math.round(mins / 60) });
  else countdown = t("upcoming.inDays", { count: Math.round(mins / (60 * 24)) });
  return `${dateLabel} · ${countdown}`;
}

export default function UpcomingCallCard({ call, onRefresh }) {
  const { t } = useTranslation();
  const [skipping, setSkipping] = useState(false);
  const { id, title, timestamp, platform } = call;

  const handleDontJoin = async () => {
    setSkipping(true);
    try {
      await skipScheduledCall({ callId: id });
      onRefresh?.();
    } catch (err) {
      console.error("Failed to skip call:", err);
      setSkipping(false);
    }
  };

  return (
    <Card sx={{ mb: 2, border: "1px solid #c7d2fe", bgcolor: "#f5f7ff" }} elevation={0}>
      <CardContent sx={{ p: 2.5, "&:last-child": { pb: 2.5 } }}>
        <Box display="flex" justifyContent="space-between" alignItems="flex-start" gap={2} flexWrap="wrap">
          <Box sx={{ minWidth: 0 }}>
            <Box display="flex" alignItems="center" gap={1} mb={0.5}>
              <EventAvailableIcon sx={{ fontSize: 18, color: "#4338ca" }} />
              <Typography sx={{ fontWeight: 600, color: "#111827", fontSize: 15 }}>
                {title || t("upcoming.untitled")}
              </Typography>
            </Box>
            <Typography sx={{ color: "#4338ca", fontSize: 13, fontWeight: 500 }}>
              {formatWhen(timestamp, t)}
            </Typography>
            <Box display="flex" alignItems="center" gap={1} mt={1} flexWrap="wrap">
              {platform && (
                <Chip
                  icon={<VideocamIcon sx={{ fontSize: 14 }} />}
                  label={PLATFORM_LABELS[platform] || platform}
                  size="small"
                  sx={{ bgcolor: "#ffffff", color: "#4338ca", fontSize: 11, fontWeight: 500 }}
                />
              )}
              <Typography sx={{ color: "#6b7280", fontSize: 12 }}>
                {t("upcoming.reportAfter")}
              </Typography>
            </Box>
          </Box>
          <Button
            size="small"
            startIcon={skipping ? <CircularProgress size={14} /> : <CloseIcon sx={{ fontSize: 16 }} />}
            disabled={skipping}
            onClick={handleDontJoin}
            sx={{ textTransform: "none", color: "#6b7280", flexShrink: 0 }}
          >
            {t("upcoming.dontJoin")}
          </Button>
        </Box>
      </CardContent>
    </Card>
  );
}
